import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

type VerifyResp = {
  valid: boolean;
  courseId?: number;
  type?: "single" | "shared";
  remainingUses?: number | null; // null = unlimited/unknown
  message?: string;
};

export default function LegacyRegister() {
  const navigate = useNavigate();
  const qs = new URLSearchParams(useLocation().search);
  const token = qs.get("token");

  const [verify, setVerify] = useState<VerifyResp | null>(null);
  const [loadingVerify, setLoadingVerify] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    whatsappNumber: "",
  });

  const API = process.env.REACT_APP_API_BASE_URL;

  const fetchVerify = async () => {
    if (!token) {
      navigate("/courses", { replace: true });
      return;
    }
    setLoadingVerify(true);
    setErr(null); // Clear any previous errors
    try {
      const res = await fetch(
        `${API}/api/auth/verify-legacy-token?token=${encodeURIComponent(
          token
        )}`,
        {
          credentials: "include",
        }
      );
      const data: VerifyResp = await res.json();
      setVerify(data);
    } catch {
      setVerify({ valid: false, message: "Network error while checking link" });
    } finally {
      setLoadingVerify(false);
    }
  };

  useEffect(() => {
    fetchVerify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setSubmitting(true);
    setErr(null);
    try {
      const res = await fetch(`${API}/api/public/legacy/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          token,
          firstName: form.firstName,
          lastName: form.lastName,
          email: form.email,
          password: form.password,
          whatsappNumber: form.whatsappNumber || null,
        }),
      });
      if (res.ok) {
        navigate("/registration-success?legacy=1", { replace: true });
      } else {
        const txt = await res.text();
        setErr(txt || "Registration failed. Please try again.");
      }
    } catch {
      setErr("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) return null;

  return (
    <div className="max-w-xl mx-auto p-6 space-y-4">
      <h1 className="text-2xl font-semibold">Legacy Student Registration</h1>

      {/* Verify banner */}
      <div className="flex items-center justify-between p-3 border rounded">
        {loadingVerify ? (
          <span>Checking your link…</span>
        ) : verify?.valid ? (
          <div className="text-sm">
            <div className="font-medium text-green-700">Link verified</div>
            <div className="text-gray-700">
              Type: {verify.type === "single" ? "Single-use" : "Shared"}
              {verify.type === "shared" && (
                <>
                  {" · "}
                  Uses:{" "}
                  {verify.remainingUses == null
                    ? "unlimited"
                    : verify.remainingUses}
                </>
              )}
              {verify.courseId ? (
                <>
                  {" "}
                  {" · "} Course ID: {verify.courseId}
                </>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="text-sm">
            <div className="font-medium text-red-700">Link not valid</div>
            <div className="text-gray-700">
              {verify?.message || "Token is invalid or expired."}
            </div>
          </div>
        )}

        <button
          className="border rounded px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-50"
          onClick={fetchVerify}
          disabled={loadingVerify}
        >
          Recheck link
        </button>
      </div>

      {/* Error Alert - Prominent Display */}
      {err && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Registration Failed
              </h3>
              <div className="mt-2 text-sm text-red-700">{err}</div>
            </div>
            <button
              className="ml-auto flex-shrink-0 text-red-500 hover:text-red-700"
              onClick={() => setErr(null)}
            >
              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <input
            className="border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="First name"
            value={form.firstName}
            onChange={(e) =>
              setForm((f) => ({ ...f, firstName: e.target.value }))
            }
            required
            disabled={submitting || !verify?.valid}
          />
          <input
            className="border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Last name"
            value={form.lastName}
            onChange={(e) =>
              setForm((f) => ({ ...f, lastName: e.target.value }))
            }
            required
            disabled={submitting || !verify?.valid}
          />
        </div>
        <input
          className="border rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Email"
          type="email"
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          required
          disabled={submitting || !verify?.valid}
        />
        <input
          className="border rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="Password"
          type="password"
          value={form.password}
          onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
          required
          disabled={submitting || !verify?.valid}
        />
        <input
          className="border rounded px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="WhatsApp number (optional)"
          value={form.whatsappNumber}
          onChange={(e) =>
            setForm((f) => ({ ...f, whatsappNumber: e.target.value }))
          }
          disabled={submitting || !verify?.valid}
        />

        <button
          className="bg-black text-white rounded px-4 py-2 w-full hover:bg-gray-800 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          disabled={submitting || loadingVerify || !verify?.valid}
        >
          {submitting ? "Submitting..." : "Register"}
        </button>
      </form>

      {/* Helper text when link is invalid */}
      {!loadingVerify && !verify?.valid && (
        <div className="text-center text-sm text-gray-600 mt-4">
          <p>Need a valid registration link?</p>
          <p>Please contact your course administrator.</p>
        </div>
      )}
    </div>
  );
}
